import { useEffect, useState } from "react";
import { Calculator, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { fmtPrice, fmtNum } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const GROUP_LABELS = {
  fixed_pct: "Fixed percentage",
  atr_based: "ATR-based (volatility)",
  support_based: "Support levels",
};

export const StopLossCalculator = () => {
  const [universe, setUniverse] = useState([]);
  const [ticker, setTicker] = useState("");
  const [entry, setEntry] = useState("");
  const [account, setAccount] = useState("");
  const [riskPct, setRiskPct] = useState("1");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/market/universe").then((r) => setUniverse(r.data.stocks));
  }, []);

  const onTicker = (v) => {
    setTicker(v);
    const u = universe.find((x) => x.ticker === v);
    if (u?.price) setEntry(String(u.price));
    setResult(null);
  };

  const calculate = async () => {
    if (!ticker) return;
    setLoading(true);
    try {
      const params = {};
      if (entry) params.entry_price = Number(entry);
      if (account) params.account_size = Number(account);
      if (account && riskPct) params.risk_pct = Number(riskPct);
      const { data } = await api.get(`/risk/stoploss/${ticker}`, { params });
      setResult(data);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="widget" data-testid="stoploss-calculator">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <Calculator className="w-4 h-4 text-primary" />
        <span className="overline-label">Stop-loss calculator</span>
      </div>
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Ticker</Label>
            <Select value={ticker} onValueChange={onTicker}>
              <SelectTrigger className="rounded-sm bg-background h-9 text-sm" data-testid="stoploss-ticker-select">
                <SelectValue placeholder="Pick a stock" />
              </SelectTrigger>
              <SelectContent className="rounded-sm max-h-72">
                {universe.map((u) => (
                  <SelectItem key={u.ticker} value={u.ticker}><span className="font-mono">{u.ticker}</span> · {u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Entry price</Label>
            <Input type="number" step="any" className="rounded-sm bg-background h-9 font-mono text-sm" data-testid="stoploss-entry-input"
              value={entry} onChange={(e) => setEntry(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Account size (optional)</Label>
            <Input type="number" step="any" placeholder="e.g. 10000" className="rounded-sm bg-background h-9 font-mono text-sm" data-testid="stoploss-account-input"
              value={account} onChange={(e) => setAccount(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Max risk % / trade</Label>
            <Select value={riskPct} onValueChange={setRiskPct}>
              <SelectTrigger className="rounded-sm bg-background h-9 text-sm" data-testid="stoploss-risk-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-sm">
                {["0.5", "1", "2", "3", "5"].map((v) => <SelectItem key={v} value={v}>{v}%</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={calculate} disabled={!ticker || loading} data-testid="stoploss-calculate-button"
          className="rounded-sm bg-primary hover:bg-primary/85 text-white h-9">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Calculate levels"}
        </Button>

        {result && (
          <div className="space-y-4 fade-up" data-testid="stoploss-results">
            <div className="text-xs font-mono text-muted-foreground">
              {result.ticker} · current {fmtPrice(result.current_price, result.currency)} · entry {fmtPrice(result.entry_price, result.currency)} ·
              ATR14 {result.atr14 != null ? fmtNum(result.atr14) : "—"}{result.atr_approximated_from_closes ? " (approx from closes)" : ""}
            </div>
            {Object.entries(result.methods).map(([group, levels]) => levels.length > 0 && (
              <div key={group}>
                <div className="overline-label mb-2">{GROUP_LABELS[group] || group}</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="px-3 py-2 overline-label">Level</th>
                      <th className="px-3 py-2 overline-label text-right">Stop</th>
                      <th className="px-3 py-2 overline-label text-right">Distance</th>
                      <th className="px-3 py-2 overline-label text-right">Suggested shares</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {levels.map((l) => (
                      <tr key={l.label} className="border-b border-border/50 even:bg-white/[0.02]" data-testid={`stoploss-level-${group}`}>
                        <td className="px-3 py-2 text-gray-300 font-body">{l.label}{!l.usable && <span className="text-yellow-500 text-xs ml-2">above entry</span>}</td>
                        <td className="px-3 py-2 text-right text-white">{fmtPrice(l.stop, result.currency)}</td>
                        <td className="px-3 py-2 text-right text-gray-300">{fmtNum(l.distance_pct, 1)}%</td>
                        <td className="px-3 py-2 text-right text-gray-300">{l.suggested_shares != null ? l.suggested_shares.toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">{result.note}</p>
            <p className="text-xs text-muted-foreground pt-2 border-t border-white/10">This is AI-generated research, not financial advice.</p>
          </div>
        )}
      </div>
    </div>
  );
};
